import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lightbulb, PanelLeft, PanelRight, Zap, Thermometer, Lock, Power, Sun, Moon } from 'lucide-react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { useTranslation } from 'react-i18next';

export const SmartHomeVisualizerModern = () => {
  const { t } = useTranslation();
  const [lightsOn, setLightsOn] = useState(false);
  const [curtainsOpen, setCurtainsOpen] = useState(false);
  const [temperature, setTemperature] = useState(22);
  const [securityActive, setSecurityActive] = useState(true);

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  };

  // Floating animation for control cards
  const floatingAnimation = (delay = 0) => ({
    y: [0, -10, 0],
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: "easeInOut",
      delay
    }
  });

  // Particle animation config
  const particleAnimation = {
    initial: { scale: 0, opacity: 0 },
    animate: { 
      scale: [0, 1, 0],
      opacity: [0, 1, 0],
      transition: { duration: 2, repeat: Infinity }
    }
  };

  return (
    <section 
      id="solutions" 
      className="relative py-24 bg-gradient-to-b from-[#050505] to-[#0a0a0a] overflow-hidden"
    >
      {/* Animated Background Effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div 
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.03, 0.06, 0.03]
          }}
          transition={{ duration: 8, repeat: Infinity }}
          className="absolute top-20 left-10 w-96 h-96 bg-[#C5A35C] rounded-full blur-3xl"
        />
        <motion.div 
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.03, 0.06, 0.03]
          }}
          transition={{ duration: 8, repeat: Infinity, delay: 4 }}
          className="absolute bottom-20 right-10 w-96 h-96 bg-[#C5A35C] rounded-full blur-3xl"
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
          className="text-center mb-16"
        >
          <motion.div
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <Badge className="mb-4 bg-[#C5A35C]/10 text-[#C5A35C] border-[#C5A35C]/30 px-4 py-2">
              <Zap className="w-4 h-4 mr-2 inline" />
              {t('visualizer.badge')}
            </Badge>
          </motion.div>
          
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {t('visualizer.title')}{' '}
            <span className="text-[#C5A35C]">{t('visualizer.title.highlight')}</span>
          </h2>
          
          <p className="text-lg text-gray-400 max-w-2xl mx-auto" style={{ fontFamily: "'Manrope', sans-serif" }}>
            {t('visualizer.description')}
          </p>
        </motion.div>

        {/* Automation Demo Videos Gallery */}

        {/* Main Visualizer */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="relative"
        >
          {/* Isometric House Visualization */}
          <motion.div 
            variants={fadeInUp}
            className="relative max-w-5xl mx-auto mb-12"
          >
            <Card className="bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] border-white/10 backdrop-blur-xl overflow-hidden relative">
              {/* Background ambient effects */}
              <div className="absolute inset-0 overflow-hidden">
                <motion.div
                  animate={{
                    scale: [1, 1.2, 1],
                    opacity: [0.05, 0.1, 0.05]
                  }}
                  transition={{ duration: 8, repeat: Infinity }}
                  className="absolute top-0 left-1/4 w-96 h-96 bg-[#C5A35C] rounded-full blur-3xl"
                />
                <motion.div
                  animate={{
                    scale: [1.2, 1, 1.2],
                    opacity: [0.05, 0.1, 0.05]
                  }}
                  transition={{ duration: 8, repeat: Infinity, delay: 4 }}
                  className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#C5A35C] rounded-full blur-3xl"
                />
              </div>

              {/* Main content */}
              <div className="relative z-10 p-8 sm:p-12">
                {/* Isometric House Container */}
                <div className="relative flex justify-center items-center min-h-[500px]">
                  {/* Central Isometric House */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8, rotateY: -20 }}
                    animate={{ opacity: 1, scale: 1, rotateY: 0 }}
                    transition={{ duration: 1, type: "spring" }}
                    className="relative"
                  >
                    <img
                      src="https://static.prod-images.emergentagent.com/jobs/e4fe7836-aa82-4d8f-9622-45c6138206f4/images/99643cceb287396d75211c509be119674da910022fb67c580cb10d5d5356a43c.jpeg"
                      alt="Casa inteligente isométrica"
                      className="w-full max-w-md h-auto drop-shadow-2xl"
                    />
                    
                    {/* Dynamic lighting overlay on house */}
                    <AnimatePresence>
                      {lightsOn && (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 0.4 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.5 }}
                          className="absolute inset-0 bg-gradient-radial from-yellow-400/60 via-transparent to-transparent blur-xl pointer-events-none"
                        />
                      )}
                    </AnimatePresence>
                  </motion.div>

                  {/* Floating Control Icons Around House */}
                  
                  {/* Light Control - Top Left */}
                  <motion.div
                    animate={floatingAnimation(0)}
                    className="absolute top-8 left-8 sm:left-16"
                  >
                    <motion.button
                      onClick={() => setLightsOn(!lightsOn)}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                      className="relative group"
                    >
                      {/* Glow effect */}
                      <AnimatePresence>
                        {lightsOn && (
                          <motion.div
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ 
                              scale: [1, 1.5, 1],
                              opacity: [0.5, 0.8, 0.5]
                            }}
                            exit={{ scale: 0.8, opacity: 0 }}
                            transition={{ duration: 2, repeat: Infinity }}
                            className="absolute inset-0 bg-yellow-400 rounded-full blur-2xl"
                          />
                        )}
                      </AnimatePresence>

                      {/* Icon container */}
                      <div className={`relative w-20 h-20 rounded-2xl flex items-center justify-center transition-all duration-500 ${
                        lightsOn 
                          ? 'bg-gradient-to-br from-yellow-400 to-[#C5A35C] shadow-2xl shadow-yellow-500/50' 
                          : 'bg-white/5 border-2 border-white/20 hover:border-[#C5A35C]'
                      }`}>
                        <Lightbulb 
                          className={`w-10 h-10 transition-all duration-500 ${
                            lightsOn ? 'text-black' : 'text-white'
                          }`}
                          fill={lightsOn ? 'currentColor' : 'none'}
                        />
                        
                        {/* Particle effects when active */}
                        {lightsOn && (
                          <>
                            {[...Array(6)].map((_, i) => (
                              <motion.div
                                key={`light-particle-${i}`}
                                className="absolute w-1 h-1 bg-yellow-300 rounded-full"
                                initial={{ 
                                  x: 0, 
                                  y: 0,
                                  opacity: 0 
                                }}
                                animate={{ 
                                  x: Math.cos(i * 60 * Math.PI / 180) * 40,
                                  y: Math.sin(i * 60 * Math.PI / 180) * 40,
                                  opacity: [0, 1, 0]
                                }}
                                transition={{ 
                                  duration: 2,
                                  delay: i * 0.1,
                                  repeat: Infinity
                                }}
                              />
                            ))}
                          </>
                        )}
                      </div>

                      {/* Label */}
                      <motion.div
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap"
                      >
                        <span className="text-xs font-medium text-white bg-black/50 px-2 py-1 rounded">
                          {lightsOn ? t('visualizer.lights.on') : t('visualizer.lights.off')}
                        </span>
                      </motion.div>
                    </motion.button>
                  </motion.div>

                  {/* Curtain Control - Top Right */}
                  <motion.div
                    animate={floatingAnimation(0.5)}
                    className="absolute top-8 right-8 sm:right-16"
                  >
                    <motion.button
                      onClick={() => setCurtainsOpen(!curtainsOpen)}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                      className="relative group"
                    >
                      {/* Glow effect */}
                      <AnimatePresence>
                        {curtainsOpen && (
                          <motion.div
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ 
                              scale: [1, 1.5, 1],
                              opacity: [0.4, 0.7, 0.4]
                            }}
                            exit={{ scale: 0.8, opacity: 0 }}
                            transition={{ duration: 2, repeat: Infinity }}
                            className="absolute inset-0 bg-blue-400 rounded-full blur-2xl"
                          />
                        )}
                      </AnimatePresence>

                      {/* Icon container */}
                      <div className={`relative w-20 h-20 rounded-2xl flex items-center justify-center transition-all duration-500 ${
                        curtainsOpen 
                          ? 'bg-gradient-to-br from-blue-400 to-[#C5A35C] shadow-2xl shadow-blue-500/50' 
                          : 'bg-white/5 border-2 border-white/20 hover:border-[#C5A35C]'
                      }`}>
                        <div className="relative flex items-center justify-center">
                          {/* Show both panels with animation */}
                          <motion.div
                            animate={{ 
                              x: curtainsOpen ? -8 : 0,
                              opacity: curtainsOpen ? 1 : 0.7
                            }}
                            transition={{ duration: 0.5 }}
                          >
                            <PanelLeft 
                              className={`w-8 h-8 transition-all duration-500 ${
                                curtainsOpen ? 'text-white' : 'text-gray-400'
                              }`}
                            />
                          </motion.div>
                          <motion.div
                            animate={{ 
                              x: curtainsOpen ? 8 : 0,
                              opacity: curtainsOpen ? 1 : 0.7
                            }}
                            transition={{ duration: 0.5 }}
                          >
                            <PanelRight 
                              className={`w-8 h-8 transition-all duration-500 ${
                                curtainsOpen ? 'text-white' : 'text-gray-400'
                              }`}
                            />
                          </motion.div>
                        </div>

                        {/* Sliding lines animation when active */}
                        {curtainsOpen && (
                          <>
                            {[...Array(3)].map((_, i) => (
                              <motion.div
                                key={`curtain-line-${i}`}
                                className="absolute w-8 h-0.5 bg-white/40"
                                initial={{ x: -20, opacity: 0 }}
                                animate={{ 
                                  x: [20, -20],
                                  opacity: [0, 1, 0]
                                }}
                                transition={{ 
                                  duration: 1.5,
                                  delay: i * 0.2,
                                  repeat: Infinity
                                }}
                              />
                            ))}
                          </>
                        )}
                      </div>

                      {/* Label */}
                      <motion.div
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap"
                      >
                        <span className="text-xs font-medium text-white bg-black/50 px-2 py-1 rounded">
                          {curtainsOpen ? t('visualizer.curtains.open') : t('visualizer.curtains.closed')}
                        </span>
                      </motion.div>
                    </motion.button>
                  </motion.div>

                  {/* Temperature Control - Bottom Left */}
                  <motion.div
                    animate={floatingAnimation(1)}
                    className="absolute bottom-8 left-8 sm:left-16"
                  >
                    <motion.button
                      onClick={() => setTemperature(temperature === 22 ? 18 : 22)}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                      className="relative group"
                    >
                      {/* Icon container */}
                      <div className="relative w-20 h-20 rounded-2xl flex items-center justify-center bg-white/5 border-2 border-white/20 hover:border-[#C5A35C] transition-all duration-300">
                        <Thermometer className="w-10 h-10 text-[#C5A35C]" />
                        
                        {/* Temperature display */}
                        <motion.div
                          key={temperature}
                          initial={{ scale: 1.5, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          className="absolute -top-2 -right-2 bg-gradient-to-br from-[#C5A35C] to-yellow-600 text-black text-xs font-bold px-2 py-1 rounded-lg shadow-lg"
                        >
                          {temperature}°
                        </motion.div>
                      </div>

                      {/* Label */}
                      <motion.div
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap"
                      >
                        <span className="text-xs font-medium text-white bg-black/50 px-2 py-1 rounded">
                          {t('visualizer.climate')}
                        </span>
                      </motion.div>
                    </motion.button>
                  </motion.div>

                  {/* Security Control - Bottom Right */}
                  <motion.div
                    animate={floatingAnimation(1.5)}
                    className="absolute bottom-8 right-8 sm:right-16"
                  >
                    <motion.button
                      onClick={() => setSecurityActive(!securityActive)}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                      className="relative group"
                    >
                      {/* Pulse effect when active */}
                      <AnimatePresence>
                        {securityActive && (
                          <>
                            {[...Array(3)].map((_, i) => (
                              <motion.div
                                key={`security-pulse-${i}`}
                                className="absolute inset-0 border-2 border-green-400 rounded-2xl"
                                initial={{ scale: 1, opacity: 0.8 }}
                                animate={{ 
                                  scale: 1.5,
                                  opacity: 0
                                }}
                                transition={{ 
                                  duration: 2,
                                  delay: i * 0.6,
                                  repeat: Infinity
                                }}
                              />
                            ))}
                          </>
                        )}
                      </AnimatePresence>

                      {/* Icon container */}
                      <div className={`relative w-20 h-20 rounded-2xl flex items-center justify-center transition-all duration-300 ${
                        securityActive 
                          ? 'bg-gradient-to-br from-green-500 to-emerald-600 shadow-2xl shadow-green-500/50' 
                          : 'bg-white/5 border-2 border-white/20 hover:border-[#C5A35C]'
                      }`}>
                        <Lock 
                          className={`w-10 h-10 ${
                            securityActive ? 'text-white' : 'text-gray-400'
                          }`}
                        />
                      </div>

                      {/* Label */}
                      <motion.div
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap"
                      >
                        <span className={`text-xs font-medium px-2 py-1 rounded ${
                          securityActive 
                            ? 'text-green-400 bg-green-900/50' 
                            : 'text-white bg-black/50'
                        }`}>
                          {securityActive ? t('visualizer.security.active') : t('visualizer.security.inactive')}
                        </span>
                      </motion.div>
                    </motion.button>
                  </motion.div>

                  {/* Center Status Badge */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.5 }}
                    className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 pointer-events-none z-20"
                  >
                    <div className="bg-black/80 backdrop-blur-xl border border-[#C5A35C]/30 rounded-2xl px-6 py-3 shadow-2xl">
                      <div className="flex items-center gap-2">
                        <motion.div
                          animate={{ 
                            scale: [1, 1.2, 1],
                            opacity: [0.5, 1, 0.5]
                          }}
                          transition={{ duration: 2, repeat: Infinity }}
                          className="w-2 h-2 bg-[#C5A35C] rounded-full"
                        />
                        <span className="text-white text-sm font-medium">{t('visualizer.system.active')}</span>
                      </div>
                    </div>
                  </motion.div>
                </div>

                {/* Stats Bar */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                  className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4"
                >
                  <div className="bg-white/5 rounded-xl p-4 text-center">
                    <Power className="w-6 h-6 text-[#C5A35C] mx-auto mb-2" />
                    <p className="text-2xl font-bold text-white">{lightsOn ? t('visualizer.stats.on') : t('visualizer.stats.off')}</p>
                    <p className="text-xs text-gray-400">{t('visualizer.stats.lighting')}</p>
                  </div>
                  <div className="bg-white/5 rounded-xl p-4 text-center">
                    {curtainsOpen ? <Sun className="w-6 h-6 text-blue-400 mx-auto mb-2" /> : <Moon className="w-6 h-6 text-gray-400 mx-auto mb-2" />}
                    <p className="text-2xl font-bold text-white">{curtainsOpen ? t('visualizer.stats.day') : t('visualizer.stats.night')}</p>
                    <p className="text-xs text-gray-400">{t('visualizer.stats.environment')}</p>
                  </div>
                  <div className="bg-white/5 rounded-xl p-4 text-center">
                    <Thermometer className="w-6 h-6 text-[#C5A35C] mx-auto mb-2" />
                    <p className="text-2xl font-bold text-white">{temperature}°C</p>
                    <p className="text-xs text-gray-400">{t('visualizer.stats.temperature')}</p>
                  </div>
                  <div className="bg-white/5 rounded-xl p-4 text-center">
                    <Lock className={`w-6 h-6 mx-auto mb-2 ${securityActive ? 'text-green-400' : 'text-gray-400'}`} />
                    <p className="text-2xl font-bold text-white">{securityActive ? t('visualizer.stats.active') : t('visualizer.stats.off')}</p>
                    <p className="text-xs text-gray-400">{t('visualizer.stats.security')}</p>
                  </div>
                </motion.div>
              </div>
            </Card>
          </motion.div>
        </motion.div>

        {/* Bottom Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="mt-8 text-center"
        >
          <p className="text-gray-500 text-sm">
            💡 {t('visualizer.footer')}
          </p>
        </motion.div>
      </div>
    </section>
  );
};
